import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HiArrowLeft, HiMail, HiPhone, HiOfficeBuilding, HiLocationMarker, HiTicket, HiDownload, HiUsers, HiSwitchHorizontal } from 'react-icons/hi';
import { Button, Card, CardHeader, Table, Badge, Select } from '../components/ui';
import { getCustomer, updateCustomer } from '../services/customers.api';
import { listPartners } from '../services/partners.api';
import { getCustomerLicenses, downloadOfflineLicense } from '../services/licenses.api';
import { getErrorMessage } from '../services/api';
import { formatDate } from '../utils/helpers';
import { useAuthStore } from '../store/authStore';
import type { License } from '../types';

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const canReassign = user?.role === 'ADMIN' || user?.role === 'OWNER';
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [partnerSel, setPartnerSel] = useState<string | null>(null);

  const handleDownloadOffline = async (license: License) => {
    try {
      setDownloadingId(license.id);
      await downloadOfflineLicense(license.id, license.licenseKey);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDownloadingId(null);
    }
  };

  const { data: customer, isLoading: customerLoading } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => getCustomer(id!),
    enabled: !!id,
  });

  const { data: licenses, isLoading: licensesLoading } = useQuery({
    queryKey: ['customerLicenses', id],
    queryFn: () => getCustomerLicenses(id!),
    enabled: !!id,
  });

  const { data: partnersData } = useQuery({
    queryKey: ['partners', 'dropdown'],
    queryFn: () => listPartners({ limit: 100 }),
    enabled: canReassign,
  });

  const reassignMutation = useMutation({
    mutationFn: (partnerId: string | null) => updateCustomer(id!, { partnerId }),
    onSuccess: () => {
      toast.success('Partner aktualisiert');
      setPartnerSel(null);
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  if (customerLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Customer not found</p>
        <Button variant="secondary" onClick={() => navigate('/customers')} className="mt-4">
          Back to Customers
        </Button>
      </div>
    );
  }

  const licenseColumns = [
    {
      key: 'licenseKey',
      header: 'License Key',
      render: (l: License) => (
        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
          {l.licenseKey.substring(0, 8)}...
        </code>
      ),
    },
    {
      key: 'product',
      header: 'Product',
      render: (l: License) => l.product?.name || '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (l: License) => <Badge status={l.status}>{l.status}</Badge>,
    },
    {
      key: 'deviceCount',
      header: 'Devices',
      render: (l: License) => `${l._count?.installations || 0} / ${l.deviceCount}`,
    },
    {
      key: 'expiresAt',
      header: 'Expires',
      render: (l: License) => formatDate(l.expiresAt),
    },
    {
      key: 'offline',
      header: 'Offline-Lizenz',
      render: (l: License) =>
        l.offlineIssuedAt ? (
          <Button
            variant="ghost"
            size="sm"
            isLoading={downloadingId === l.id}
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadOffline(l);
            }}
            title="Offline-Lizenz herunterladen"
          >
            <HiDownload className="w-4 h-4" />
          </Button>
        ) : (
          <span className="text-xs text-gray-400" title="Hardware-ID fehlt">–</span>
        ),
    },
  ];

  return (
    <div>
      <Button
        variant="ghost"
        onClick={() => navigate('/customers')}
        className="mb-4"
      >
        <HiArrowLeft className="w-4 h-4 mr-2" />
        Back to Customers
      </Button>

      <CardHeader
        title={customer.name}
        description="Customer details and licenses"
      />

      {/* Customer Info */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiMail className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">{customer.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiPhone className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="font-medium">{customer.phone || '-'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiOfficeBuilding className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Company</p>
              <p className="font-medium">{customer.company || '-'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiLocationMarker className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Address</p>
              <p className="font-medium">{customer.address || '-'}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-6 text-sm text-gray-500">
          <span>Created: {formatDate(customer.createdAt)}</span>
          <span>Partner: {customer.partner?.name || 'None'}</span>
          <span>
            Status:{' '}
            <Badge status={customer.isActive ? 'ACTIVE' : 'SUSPENDED'}>
              {customer.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </span>
        </div>
      </Card>

      {/* Reassign partner (Admin/Owner) */}
      {canReassign && (
        <Card className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2 mb-1">
            <HiSwitchHorizontal className="w-5 h-5 text-gray-400" />
            Partner zuweisen
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Kunden einem anderen Partner zuweisen. Bestehende Lizenzen bleiben erhalten.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 max-w-sm">
              <Select
                label="Partner"
                options={[
                  { value: '', label: 'Kein Partner' },
                  ...(partnersData?.data.map((p) => ({ value: p.id, label: p.name })) || []),
                ]}
                value={partnerSel ?? (customer.partner?.id || '')}
                onChange={(e) => setPartnerSel(e.target.value)}
              />
            </div>
            <Button
              onClick={() => reassignMutation.mutate((partnerSel ?? (customer.partner?.id || '')) || null)}
              isLoading={reassignMutation.isPending}
              disabled={(partnerSel ?? (customer.partner?.id || '')) === (customer.partner?.id || '')}
            >
              Speichern
            </Button>
          </div>
        </Card>
      )}

      {/* Users with access */}
      <Card className="mb-6" padding={false}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <HiUsers className="w-5 h-5 text-gray-400" />
            Users mit Zugriff ({customer.users?.length || 0})
          </h3>
        </div>
        {customer.users && customer.users.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {customer.users.map((u) => (
              <li key={u.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="font-medium text-gray-900">
                    {`${u.firstName} ${u.lastName}`.trim() || u.email}
                  </p>
                  <p className="text-sm text-gray-500">{u.email}</p>
                </div>
                <Badge status={u.isActive ? 'ACTIVE' : 'SUSPENDED'}>
                  {u.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-6 text-sm text-gray-500">
            Kein Benutzer mit diesem Kunden verknüpft.
          </p>
        )}
      </Card>

      {/* Licenses */}
      <Card padding={false}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <HiTicket className="w-5 h-5 text-gray-400" />
            Licenses ({licenses?.length || 0})
          </h3>
        </div>
        <Table
          columns={licenseColumns}
          data={licenses || []}
          keyExtractor={(license) => license.id}
          isLoading={licensesLoading}
          emptyMessage="No licenses found for this customer"
          onRowClick={(license) => navigate(`/licenses/${license.id}`)}
        />
      </Card>
    </div>
  );
}
